import React, { useCallback, useMemo } from 'react';
import Search, { SearchEvent } from 'carbon-react/lib/components/search';
import { Option, Select } from 'carbon-react/lib/components/select';
import Box from 'carbon-react/lib/components/box';

import { useIntl } from '@futrli/localisation';

import { useCustomersAndSuppliersContext } from '../../context/customers-and-suppliers-context';
import { parseSortOptionValue, toSortOptionValue } from '../../../../../utils/list-filters/list-filter-utils';
import * as styles from './filter-header.scss';
import { FiltersButton } from './filter-button';

export const FilterHeader = () => {
  const intl = useIntl();

  const {
    searchTerm,
    setSearchTerm,
    typeShown,
    setTypeShown,
    period,
    setPeriod,
    periodOptions,
    sortConfig,
    setSortConfig,
    sortOptions,
    activeFilterCount,
    openFiltersSidebar,
    clearAllFilters
  } = useCustomersAndSuppliersContext();

  const handleSearchChange = useCallback(
    (event: SearchEvent) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleTypeShownChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTypeShown(event.target.value as 'all' | 'customer' | 'supplier');
    },
    [setTypeShown]
  );

  const handlePeriodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPeriod(event.target.value);
    },
    [setPeriod]
  );

  const handleSortChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSortConfig(parseSortOptionValue(event.target.value));
    },
    [setSortConfig]
  );

  const sortValue = useMemo(() => toSortOptionValue(sortConfig), [sortConfig]);

  return (
    <div className={styles.filterHeader}>
      <div className={styles.filterHeaderRow}>
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Box mr={2} width="300px">
            <Search
              id="cas-search"
              label={intl.formatMessage({ id: 'common.search', defaultMessage: 'Search' })}
              value={searchTerm}
              onChange={handleSearchChange}
              inputWidth={100}
            />
          </Box>
          <Box mr={2} className={styles.selectTypeShown}>
            <Select
              id="cas-type-shown"
              value={typeShown}
              onChange={handleTypeShownChange}
              size="medium"
              label={intl.formatMessage({
                id: 'predict.type_shown',
                defaultMessage: 'Type shown'
              })}
              aria-label={intl.formatMessage({
                id: 'predict.type_shown',
                defaultMessage: 'Type shown'
              })}
            >
              <Option
                value="all"
                text={intl.formatMessage({
                  id: 'predict.customers_and_suppliers',
                  defaultMessage: 'Customers & Suppliers'
                })}
              />
              <Option
                value="customer"
                text={intl.formatMessage({
                  id: 'predict.customers',
                  defaultMessage: 'Customers'
                })}
              />
              <Option
                value="supplier"
                text={intl.formatMessage({
                  id: 'predict.suppliers',
                  defaultMessage: 'Suppliers'
                })}
              />
            </Select>
          </Box>
          {/* Period menu */}
          <Box mr={2} className={styles.selectPeriod}>
            <Select
              id="cas-period"
              value={period}
              onChange={handlePeriodChange}
              size="medium"
              label={intl.formatMessage({
                id: 'predict.show_data_for',
                defaultMessage: 'Show data for'
              })}
              aria-label={intl.formatMessage({
                id: 'predict.show_data_for',
                defaultMessage: 'Show data for'
              })}
            >
              {periodOptions.map(opt => (
                <Option key={opt.value} value={opt.value} text={opt.name} />
              ))}
            </Select>
          </Box>
          {/* Filters button */}
          <Box mr={2}>
            <FiltersButton
              activeCount={activeFilterCount}
              onManageFilters={openFiltersSidebar}
              onClearAll={clearAllFilters}
            />
          </Box>
        </Box>
        {/* Sort by menu */}
        <Box className={styles.selectSortBy}>
          <Select
            id="cas-sort-by"
            value={sortValue}
            onChange={handleSortChange}
            size="medium"
            label={intl.formatMessage({
              id: 'predict.sort_by',
              defaultMessage: 'Sort by'
            })}
            aria-label={intl.formatMessage({
              id: 'predict.sort_by',
              defaultMessage: 'Sort by'
            })}
          >
            {sortOptions.map(opt => (
              <Option key={opt.value} value={opt.value} text={opt.label} />
            ))}
          </Select>
        </Box>
      </div>
    </div>
  );
};
